import { actionCreators } from '../../store/actions';
import ReduxActions from '../../store/ReduxActions';
import {
  ErrorHandlerInterface,
  GenericOrder,
  PaymentIntent,
  PaymentOptionPayloadInterface,
  PaymentOptionPropertiesInterface,
  PaymentSetupIntent,
  PaymentSetupIntentPayload,
} from '../exports/Interfaces';
import BackendApiClient from './BackendApiClient';
import { DataWrappedResponse, Response } from '../exports/Types';
import { PaymentMethod } from '../exports/Constants';
import { PaymentMethodsResponse } from '@adyen/adyen-web';

export type PaymentIntentBody = {
  company_id: number;
  payment_method_id: PaymentMethod;
  force_payment_method?: boolean;
};

export type GooglePayPaymentIntentBody = {
  company_id: number;
  amount: number;
};

class PaymentManager {
  public getPaymentOptions = async (companyId: number): Promise<DataWrappedResponse<PaymentIntent>> => {
    const request = await BackendApiClient.request({
      method: 'GET',
      url: '/payment-options',
      params: {
        company_id: companyId,
      },
    });
    const { success, response } = request;

    if (success) {
      ReduxActions.dispatch(actionCreators.paymentOptions.setPaymentOptions(response.data.data));
    }

    return request;
  };

  removePaymentOption = async (id?: number) => {
    const request = await BackendApiClient.request({
      method: 'DELETE',
      url: `/payment-options/${id}`,
    });
    const { response, success } = request;

    if (success) {
      this.removePaymentOptionRedux(id, response.data.default_payment_option);
    }

    return request;
  };

  createPaymentOption = async (data: PaymentOptionPayloadInterface) => {
    const request = await BackendApiClient.request({
      method: 'POST',
      url: '/payment-options',
      data,
    });
    const { response, success } = request;

    if (success) {
      ReduxActions.dispatch(actionCreators.paymentOptions.addPaymentOption(response.data.data));
    }

    return request;
  };

  removePaymentOptionRedux = (id: number, newDefaultPaymentOption: PaymentOptionPropertiesInterface) => {
    const payload = {
      removePaymentOptionId: id,
      newDefaultPaymentOption,
    };
    const action = actionCreators.paymentOptions.removePaymentOption(payload);

    ReduxActions.dispatch(action);
  };

  public createPaymentSetupIntent = async (data: PaymentSetupIntentPayload): Promise<Response<PaymentSetupIntent>> =>
    BackendApiClient.request({
      method: 'POST',
      url: '/payment-setup-intents',
      data,
    });

  /**
   * The CustomerSession behind Stripe's own "save payment details" box in the Payment Element, for a
   * logged-in customer. `client_secret` is null where the restaurant does not take the Stripe Elements
   * checkout or Stripe refused; the Element is then drawn without the box.
   */
  public createStripeCustomerSession = async (
    companyId: number,
  ): Promise<Response<{ client_secret: null | string; expires_at: null | number }>> =>
    BackendApiClient.request({
      method: 'POST',
      url: '/stripe/customer-sessions',
      data: { company_id: companyId },
    });

  public createStripePaymentIntent = async (data: PaymentIntentBody): Promise<DataWrappedResponse<PaymentIntent>> =>
    BackendApiClient.request({
      method: 'POST',
      url: '/stripe/payment-intents',
      data,
    });

  public createPaypalStandaloneOrder = async (data: {
    company_id: number;
    amount: number;
  }): Promise<Response<{ id: string }>> =>
    BackendApiClient.request({
      method: 'POST',
      url: '/paypal/orders',
      data,
    });

  public updateStripePaymentIntent = async (
    id: string,
    data: PaymentIntentBody,
  ): Promise<DataWrappedResponse<PaymentIntent>> =>
    BackendApiClient.request({
      method: 'PUT',
      url: `/stripe/payment-intents/${id}`,
      data,
    });

  public submitAdyenPaymentDetails = async (
    data: object,
    errorHandlers?: ErrorHandlerInterface,
  ): Promise<DataWrappedResponse<GenericOrder>> =>
    BackendApiClient.request(
      {
        method: 'POST',
        url: '/adyen/payment-details',
        data,
      },
      undefined,
      errorHandlers,
    );

  /**
   * Settles a pending Spreedly 3DS charge synchronously after the challenge
   * completes client-side. 200 = settled and paid (full order in the body); 409 =
   * settled and refused (same order body); 402 = still pending at Spreedly; 404 =
   * unknown token or token/order mismatch.
   */
  public submitSpreedlyPaymentDetails = async (
    data: { company_id: number; transaction_token: string; order_uuid: string },
    errorHandlers?: ErrorHandlerInterface,
  ): Promise<DataWrappedResponse<GenericOrder>> =>
    BackendApiClient.request(
      {
        method: 'POST',
        url: '/spreedly/payment-details',
        data,
      },
      undefined,
      errorHandlers,
    );

  /**
   * Confirms a Braintree PayPal/Venmo purchase with what its button returned. 200 = paid (full
   * order in the body); 409 = the wallet refused, or the customer cancelled; 404 = unknown token or
   * token/order mismatch.
   */
  public submitSpreedlyOffsiteConfirm = async (
    data: {
      company_id: number;
      transaction_token: string;
      order_uuid: string;
      state: string;
      payment_method_type: string;
      nonce?: string | null;
      device_data?: string | null;
      username?: string | null;
    },
    errorHandlers?: ErrorHandlerInterface,
  ): Promise<DataWrappedResponse<GenericOrder>> =>
    BackendApiClient.request(
      {
        method: 'POST',
        url: '/spreedly/offsite-confirm',
        data,
      },
      undefined,
      errorHandlers,
    );

  public getAdyenPaymentMethodsConfig = async (params: object): Promise<Response<PaymentMethodsResponse>> =>
    BackendApiClient.request({
      method: 'GET',
      url: '/adyen/payment-methods',
      params,
    });

  public getGooglePayJwt = async (params: { company_id: number; origin: string }): Promise<Response<{ jwt: string }>> =>
    BackendApiClient.request({
      method: 'GET',
      url: '/adyen/gpay-jwt',
      params,
    });

  public deleteAdyenPaymentMethod = async (id: object, data: object): Promise<Response<object>> =>
    BackendApiClient.request({
      method: 'DELETE',
      url: `/adyen/payment-methods/${id}`,
      data,
    });

  public createAdyenToken = async (data: { company_id: number; adyen_payment_data: object; return_url: string }) =>
    BackendApiClient.request({
      method: 'POST',
      url: '/adyen/tokens',
      data,
    });

  public submitAdyenTokenDetails = async (data: {
    company_id: number;
    adyen_payment_data: object;
  }): Promise<Response<object>> =>
    BackendApiClient.request({
      method: 'POST',
      url: '/adyen/tokens/details',
      data,
    });
}

export default new PaymentManager();
